/**
 * Typed fetch wrapper for the API.
 *
 * `credentials: 'same-origin'` is required for the session cookie. The app is served from the same
 * origin as the Functions runtime in both dev (Wrangler on 8783) and production, so there is no
 * CORS handling anywhere.
 */

export interface SessionUser {
  id: string;
  email: string;
  displayName: string;
}

export interface CollectionItem {
  id: string;
  name: string;
  headline: string | null;
  totalMinutes: number | null;
  prepMinutes: number | null;
  difficultyLabel: string | null;
  isPartial: boolean;
  parserTier: number;
  imageKey: string | null;
  defaultServings: number;
  isFavourite: boolean;
  addedAt: string;
}

export type JobStatus = 'queued' | 'processing' | 'done' | 'failed';

export interface ImportJob {
  id: string;
  status: JobStatus;
  stage: 'fetch' | 'parse' | 'assets' | 'persist' | null;
  attempts: number;
  maxAttempts: number;
  parserTier: number | null;
  error: string | null;
  sourceUrl: string;
  recipeId: string | null;
  createdAt: string;
  finishedAt: string | null;
}

export interface RecipeDetail {
  recipe: {
    id: string;
    slug: string | null;
    name: string;
    headline: string | null;
    description: string | null;
    difficultyLabel: string | null;
    prepMinutes: number | null;
    totalMinutes: number | null;
    servingSizeG: number | null;
    imageKey: string | null;
    sourceUrl: string;
    canonicalLink: string | null;
    isPartial: boolean;
    parserTier: number;
    averageRating: number | null;
    ratingsCount: number | null;
    createdBy: string | null;
  };
  defaultServings: number;
  isFavourite: boolean;
  yields: Array<{ servings: number; isDefault: boolean }>;
  ingredients: Array<{
    hfId: string;
    canonicalKey: string;
    name: string;
    shipped: boolean;
    imageKey: string | null;
    position: number;
  }>;
  quantities: Array<{
    servings: number;
    hfId: string;
    amount: number | null;
    unit: string | null;
    unitNormalized: string | null;
    amountBase: number | null;
  }>;
  steps: Array<{
    stepIndex: number;
    caption: string | null;
    instructionsHtml: string | null;
    instructionsText: string | null;
    imageKey: string | null;
  }>;
  nutrition: Array<{ name: string; amount: number | null; unit: string | null; position: number }>;
  allergens: Array<{ name: string; slug: string; tracesOf: boolean }>;
  tags: Array<{ name: string; slug: string; type: string | null }>;
  utensils: Array<{ name: string; type: string | null }>;
}

export interface ShoppingListSummary {
  id: string;
  name: string;
  createdAt: string;
}

export interface ShoppingListItem {
  id: string;
  displayName: string;
  unit: string;
  amount: number;
  amountText: string;
  isPantry: boolean;
  isChecked: boolean;
  sortOrder: number;
  sourceRecipeIds: string[];
}

export interface ShoppingListDetail {
  list: ShoppingListSummary;
  items: ShoppingListItem[];
  entries: Array<{ recipeId: string; servings: number }>;
}

export class ApiError extends Error {
  readonly status: number;

  constructor(message: string, status: number) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
  }
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const headers = new Headers(init.headers);
  if (init.body) headers.set('Content-Type', 'application/json');

  const response = await fetch(path, { ...init, headers, credentials: 'same-origin' });

  const raw = await response.text();
  let data: unknown = null;
  if (raw) {
    try {
      data = JSON.parse(raw);
    } catch {
      data = null;
    }
  }

  if (!response.ok) {
    const message =
      typeof data === 'object' && data !== null && 'error' in data
        ? String((data as { error: unknown }).error)
        : `Request failed (${response.status})`;
    throw new ApiError(message, response.status);
  }

  return data as T;
}

/* ------------------------------- auth ------------------------------- */

export async function fetchMe(): Promise<SessionUser | null> {
  try {
    return (await request<{ user: SessionUser }>('/api/auth/me')).user;
  } catch (error) {
    if (error instanceof ApiError && error.status === 401) return null;
    throw error;
  }
}

export async function login(email: string, password: string): Promise<SessionUser> {
  return (
    await request<{ user: SessionUser }>('/api/auth/login', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    })
  ).user;
}

export async function logout(): Promise<void> {
  await request('/api/auth/logout', { method: 'POST' });
}

/* ------------------------------ recipes ----------------------------- */

export async function fetchCollection(): Promise<CollectionItem[]> {
  return (await request<{ recipes: CollectionItem[] }>('/api/recipes')).recipes;
}

export async function fetchRecipe(id: string): Promise<RecipeDetail> {
  return request<RecipeDetail>(`/api/recipes/${encodeURIComponent(id)}`);
}

export async function deleteRecipe(id: string): Promise<void> {
  await request(`/api/recipes/${encodeURIComponent(id)}`, { method: 'DELETE' });
}

export async function updateRecipeSettings(
  id: string,
  settings: { defaultServings?: number; isFavourite?: boolean },
): Promise<void> {
  await request(`/api/recipes/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    body: JSON.stringify(settings),
  });
}

export type ImportResponse =
  | { status: 'queued' | 'already_queued'; jobId: string; job: ImportJob }
  | { status: 'already_imported'; recipeId: string };

export async function importRecipe(url: string): Promise<ImportResponse> {
  return request<ImportResponse>('/api/recipes/import', {
    method: 'POST',
    body: JSON.stringify({ url }),
  });
}

/* -------------------------------- jobs ------------------------------ */

export async function fetchJob(id: string): Promise<ImportJob> {
  return (await request<{ job: ImportJob }>(`/api/jobs/${encodeURIComponent(id)}`)).job;
}

export async function fetchJobs(): Promise<ImportJob[]> {
  return (await request<{ jobs: ImportJob[] }>('/api/jobs')).jobs;
}

export async function retryJob(id: string): Promise<ImportJob> {
  return (await request<{ job: ImportJob }>(`/api/jobs/${encodeURIComponent(id)}/retry`, { method: 'POST' })).job;
}

/* -------------------------------- lists ----------------------------- */

export async function fetchLists(): Promise<ShoppingListSummary[]> {
  return (await request<{ lists: ShoppingListSummary[] }>('/api/lists')).lists;
}

export async function fetchList(id: string): Promise<ShoppingListDetail> {
  return request<ShoppingListDetail>(`/api/lists/${encodeURIComponent(id)}`);
}

export async function createList(
  name: string,
  items: Array<{ recipeId: string; servings: number }>,
): Promise<{ id: string; itemCount: number; snapped: number }> {
  return request('/api/lists', { method: 'POST', body: JSON.stringify({ name, items }) });
}

export async function regenerateList(
  id: string,
  items: Array<{ recipeId: string; servings: number }>,
): Promise<void> {
  await request(`/api/lists/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    body: JSON.stringify({ items }),
  });
}

export async function setItemChecked(listId: string, itemId: string, isChecked: boolean): Promise<void> {
  await request(`/api/lists/${encodeURIComponent(listId)}/items/${encodeURIComponent(itemId)}`, {
    method: 'PATCH',
    body: JSON.stringify({ isChecked }),
  });
}

export async function deleteList(id: string): Promise<void> {
  await request(`/api/lists/${encodeURIComponent(id)}`, { method: 'DELETE' });
}

/* ------------------------------- images ----------------------------- */

/** Builds the local image URL for a stored asset key. Never a HelloFresh URL. */
export function imageUrl(imageKey: string | null | undefined): string | null {
  if (!imageKey) return null;
  const [recipeId, ...rest] = imageKey.split('/');
  if (!recipeId || rest.length === 0) return null;
  return `/api/images/${recipeId}/${rest.join('/')}`;
}
